import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { MaterialService } from './material.service';
import { ProctorEvent, ProctorScore, ProctorEventPayload, ProctorEventType } from '../model/proctoring.model';

/**
 * Service de proctoring REST pour surveillance anti-cheat.
 *
 * Ce service gère uniquement les appels REST :
 *  - Envoi d'événements de proctoring
 *  - Récupération du score
 *  - Upload de captures caméra
 *
 * La communication WebSocket (join_identity_capture, request_capture, capture_received_ack)
 * est gérée par WebsocketService + NgRx Store, directement dans ProctoringWebcamComponent
 * (même pattern que examination-room utilise pour join_examination_session).
 */
@Injectable()
export class ProctoringService extends KiwixiService {

  // Signal pour le score de proctoring en temps réel
  public currentScore = signal<ProctorScore | null>(null);

  // Subject pour les événements de proctoring
  private proctoringEvents$ = new Subject<ProctorEvent>();

  // Mapping des codes d'événements vers leurs labels français
  private readonly EVENT_TYPE_LABELS: Record<string, string> = {
    'camera_capture_timeout': 'Timeout capture caméra',
    'camera_obstruction': 'Caméra obstruée',
    'copy': 'Copie',
    'cut': 'Couper',
    'devtools_open': 'Outils développeur ouverts',
    'face_not_detected': 'Visage non détecté',
    'focus_lost': 'Perte de focus',
    'keystroke_anomaly': 'Anomalie frappe clavier',
    'multiple_device_fingerprint': 'Plusieurs empreintes appareil',
    'multiple_faces': 'Plusieurs visages détectés',
    'multiple_ip_address': 'Plusieurs adresses IP',
    'other': 'Autre',
    'paste': 'Coller',
    'right_click': 'Clic droit',
    'screen_capture': "Capture d'écran",
    'tab_hidden': 'Onglet masqué',
    'tab_switch': "Changement d'onglet"
  };

  constructor(
    private _http: HttpClient,
    private _router: Router,
    kiwixiGlobals: KiwixiGlobals,
    private materialService: MaterialService,
  ) {
    super(_http, _router, kiwixiGlobals, materialService);
  }

  // ---------------------------------------------------------------------------
  //  REST endpoints
  // ---------------------------------------------------------------------------

  /**
   * Envoie un événement de proctoring
   */
  sendEvent(testId: number, payload: ProctorEventPayload): Observable<ProctorEvent> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<ProctorEvent>(
      `${environment.server}lilate_exams/${testId}/proctor-events/`,
      payload,
      { headers }
    ).pipe(
      tap(event => this.proctoringEvents$.next(event)),
      catchError(this.handleErrorObservable<ProctorEvent>('sendEvent'))
    );
  }

  /**
   * Récupère le score de proctoring d'un test
   */
  getProctoringScore(lilateId: number): Observable<ProctorScore> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<ProctorScore>(
      `${environment.server}lilate_exams/${lilateId}/proctor-score/`,
      { headers }
    ).pipe(
      tap(score => this.currentScore.set(score)),
      catchError(this.handleErrorObservable<ProctorScore>('getProctoringScore'))
    );
  }

  /**
   * Récupère la liste des événements de proctoring pour un test LILATE
   */
  getProctorEvents(lilateId: number): Observable<ProctorEvent[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<ProctorEvent[]>(
      `${environment.server}lilate_exams/${lilateId}/proctor-events/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<ProctorEvent[]>('getProctorEvents'))
    );
  }

  /**
   * Récupère la liste des événements de proctoring pour un test ELATE (staff, API interne)
   * GET /api/v1/elates/{pk}/proctor-events/
   */
  getElateProctorEvents(elateId: number): Observable<ProctorEvent[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<ProctorEvent[]>(
      `${environment.server}elates/${elateId}/proctor-events/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<ProctorEvent[]>('getElateProctorEvents'))
    );
  }

  /**
   * Récupère le score de proctoring d'un test ELATE (staff, API interne)
   * GET /api/v1/elates/{pk}/proctor-score/
   */
  getElateProctorScore(elateId: number): Observable<ProctorScore> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<ProctorScore>(
      `${environment.server}elates/${elateId}/proctor-score/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<ProctorScore>('getElateProctorScore'))
    );
  }

  /**
   * Envoie un événement de proctoring pour un test FlashLevel/Elate
   * POST /api/external/v1/flashlevel/{token}/proctor-events/
   */
  sendFlashlevelEvent(elateToken: string, payload: ProctorEventPayload): Observable<ProctorEvent> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<ProctorEvent>(
      `${environment.base_server}/api/external/v1/flashlevel/${elateToken}/proctor-events/`,
      payload,
      { headers }
    ).pipe(
      tap(event => this.proctoringEvents$.next(event)),
      catchError(this.handleErrorObservable<ProctorEvent>('sendFlashlevelEvent'))
    );
  }

  /**
   * Récupère le score de proctoring d'un test FlashLevel/Elate
   * GET /api/external/v1/flashlevel/{token}/proctor-score/
   */
  getFlashlevelProctoringScore(elateToken: string): Observable<ProctorScore> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<ProctorScore>(
      `${environment.base_server}/api/external/v1/flashlevel/${elateToken}/proctor-score/`,
      { headers }
    ).pipe(
      tap(score => this.currentScore.set(score)),
      catchError(this.handleErrorObservable<ProctorScore>('getFlashlevelProctoringScore'))
    );
  }

  /**
   * Récupère la liste des types d'événements de proctoring disponibles
   */
  getEventTypes(): Observable<ProctorEventType[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<ProctorEventType[]>(
      `${environment.server}proctor-event-types/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<ProctorEventType[]>('getEventTypes'))
    );
  }

  /**
   * Upload d'une capture caméra via REST (multipart/form-data).
   * Endpoint : POST /lilate_exams/{pk}/identity/capture/  (ou /elates/{pk}/...)
   */
  uploadCapture(testId: string | number, testType: 'lilate' | 'elate', imageBlob: Blob, captureRequestId: string): Observable<any> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    const formData = new FormData();
    formData.append('file', imageBlob, 'capture.jpg');
    formData.append('capture_type', 'camera_capture');
    if (captureRequestId) {
      formData.append('capture_request_id', captureRequestId);
    }

    const endpoint = testType === 'elate'
      ? `${environment.base_server}/api/external/v1/flashlevel/${testId}/identity/capture/`
      : `${environment.server}lilate_exams/${testId}/identity/capture/`;

    return this._http.post(endpoint, formData, { headers }).pipe(
      tap(res => console.log('[ProctoringService] Capture uploadée:', res)),
      catchError(this.handleErrorObservable<any>('uploadCapture'))
    );
  }

  // ---------------------------------------------------------------------------
  //  Helpers
  // ---------------------------------------------------------------------------

  /**
   * Observable pour écouter les événements de proctoring
   */
  get events(): Observable<ProctorEvent> {
    return this.proctoringEvents$.asObservable();
  }

  /**
   * Récupère le label français d'un type d'événement
   */
  getEventTypeLabel(code: string): string {
    return this.EVENT_TYPE_LABELS[code] || code;
  }
}
